import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useTransaction, useTransactions } from "@/state/transactions";
import type {
  CompletePurchaseRequest,
  CancelPurchaseRequest,
} from "@/models/transaction";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoadingSpinner } from "@/components/ui/loading";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {

  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { useMutationTracker } from "@/hooks/useMutationTracker";
import { CopyButton } from "@/components/ui/shadcn-io/copy-button";
import { useSystemDefaults } from "@/state/system";

const statusIcons = {
  pending: Clock,
  completed: CheckCircle,
  failed: XCircle,
  cancelled: AlertTriangle,
};

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

const typeColors = {
  purchase: "bg-blue-100 text-blue-800",
  commission: "bg-purple-100 text-purple-800",
  withdrawal: "bg-orange-100 text-orange-800",
  credit: "bg-green-100 text-green-800",
  debit: "bg-red-100 text-red-800",
};

export default function TransactionDetail() {
  const { id } = useParams({ strict: false });

  const transactionId = parseInt(id || "0");
  const [completePurchaseDialogOpen, setCompletePurchaseDialogOpen] =
    useState(false);
  const [cancelPurchaseDialogOpen, setCancelPurchaseDialogOpen] =
    useState(false);

  const { transaction, dataIsLoading, dataError } =
    useTransaction(transactionId);

  const {
    completePurchase,
    cancelPurchase,
    completePurchaseLoading,
    completePurchaseError,
    completePurchaseSuccess,
    cancelPurchaseLoading,
    cancelPurchaseError,
    cancelPurchaseSuccess,
  } = useTransactions();

  const {getWalletAddress} = useSystemDefaults()

  const [txId, setTxId] = useState("");

  const handleCompletePurchase = () => {
    if (!transaction) return;

    const request: CompletePurchaseRequest = {
      txId: txId,
      transaction_id: transaction.id,
    };

    completePurchase(request);
  };

  useMutationTracker("complete-purchase").addListener(({ isSuccess }) => {
    if (isSuccess) setTxId("");
    if (isSuccess) setCompletePurchaseDialogOpen(false);
    if (isSuccess) window.location.reload();
  });

  useMutationTracker("cancel-purchase").addListener(({ isSuccess }) => {
    if (isSuccess) window.location.reload();
  });

  const handleCancelPurchase = () => {
    if (!transaction) return;

    const request: CancelPurchaseRequest = {
      transaction_id: transaction.id,
    };

    cancelPurchase(request);
  };

  const formatAmount = (amount: string, currency: string) => {
    const num = parseFloat(amount);
    return `${num.toFixed(2)} ${currency}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Handle success states
  if (completePurchaseSuccess) {
    setTimeout(() => setCompletePurchaseDialogOpen(false), 1000);
  }

  if (cancelPurchaseSuccess) {
    setTimeout(() => setCancelPurchaseDialogOpen(false), 1000);
  }

  if (dataIsLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (dataError || !transaction) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load transaction details. Please try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const StatusIcon = statusIcons[transaction.status];
  const canComplete =
    transaction.status === "pending" && transaction.type === "purchase";
  const canCancel = transaction.status === "pending";

  return (
    <div className="container mx-auto p-6 space-y-6">
      {completePurchaseError ? (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to complete purchase: {String(completePurchaseError)}
          </AlertDescription>
        </Alert>
      ) : (
        <></>
      )}

      {cancelPurchaseError ? (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to cancel purchase: {String(cancelPurchaseError)}
          </AlertDescription>
        </Alert>
      ) : (
        <></>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <StatusIcon className="w-5 h-5" />
                Transaction Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Transaction ID
                  </label>
                  <p className="text-lg font-semibold">#{transaction.id}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Type
                  </label>
                  <div className="mt-1">
                    <Badge className={typeColors[transaction.type]}>
                      {transaction.type}
                    </Badge>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Amount
                  </label>
                  <p className="text-2xl font-bold">
                    {formatAmount(transaction.amount, "$")}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Status
                  </label>
                  <div className="mt-1">
                    <Badge className={statusColors[transaction.status]}>
                      {transaction.status}
                    </Badge>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Created
                  </label>
                  <p className="text-lg">
                    {formatDate(transaction.created_at)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Linked Resource */}
          {transaction.linked_resource && (
            <Card>
              <CardHeader>
                <CardTitle>Linked Resource</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-md overflow-auto text-sm">
                  {JSON.stringify(transaction.linked_resource, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Meta Data */}
          {transaction.meta && Object.keys(transaction.meta).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-md overflow-auto text-sm">
                  {JSON.stringify(transaction.meta, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Actions Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {canComplete && (
                <Dialog
                  open={completePurchaseDialogOpen}
                  onOpenChange={setCompletePurchaseDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button className="w-full" variant="default">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Complete Purchase
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Complete Purchase</DialogTitle>
                      <DialogDescription>
                        Are you sure you want to complete this purchase? This
                        action cannot be undone.
                        <div className="flex flex-row justify-center items-center">
                          <div className="flex flex-row px-3 py-1 bg-card rounded-3xl justify-center items-center  ">
                            <p className="text-sm text-muted-foreground font-mono break-all">
                              {getWalletAddress()}
                            </p>
                            <CopyButton
                              variant={"default"}
                              className="bg-black"
                              content={String(getWalletAddress())}
                            />
                          </div>
                        </div>
                      </DialogDescription>
                    </DialogHeader>

                    <div className="mb-8">
                        <Input
                          type="text"
                          placeholder="Enter Transaction ID"
                          value={txId}
                          onChange={(e) => setTxId(e.target.value)}
                          required
                        />
                      </div>

                    <DialogFooter className="">
                      <Button
                        variant="outline"
                        className=""
                        onClick={() => setCompletePurchaseDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="mb-4"
                        onClick={handleCompletePurchase}
                        disabled={completePurchaseLoading}
                      >
                        {completePurchaseLoading
                          ? "Processing..."
                          : "Complete Purchase"}
                      </Button>
                      
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}

              {canCancel && (
                <Dialog
                  open={cancelPurchaseDialogOpen}
                  onOpenChange={setCancelPurchaseDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button className="w-full" variant="destructive">
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel Transaction
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      <DialogHeader className="space-y-3">
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1, duration: 0.2 }}
                          className="flex items-center gap-3"
                        >
                          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                            <XCircle className="w-6 h-6 text-red-600" />
                          </div>
                          <div>
                            <DialogTitle className="text-lg font-semibold">
                              Cancel Transaction
                            </DialogTitle>
                            <DialogDescription className="text-sm text-muted-foreground mt-1">
                              Transaction #{transaction.id}
                            </DialogDescription>
                          </div>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.15, duration: 0.2 }}
                          className="bg-red-50 border border-red-200 rounded-lg p-4"
                        >
                          <p className="text-sm text-red-800">
                            This will permanently cancel the transaction for{" "}
                            <span className="font-semibold">
                              {formatAmount(
                                transaction.amount,
                                transaction.currency
                              )}
                            </span>
                            . This action cannot be undone.
                          </p>
                        </motion.div>
                      </DialogHeader>
                      <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 mt-6">
                        <Button
                          variant="outline"
                          onClick={() => setCancelPurchaseDialogOpen(false)}
                          className="w-full sm:w-auto"
                          disabled={cancelPurchaseLoading}
                        >
                          Keep Transaction
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={handleCancelPurchase}
                          disabled={cancelPurchaseLoading}
                          className="w-full sm:w-auto"
                        >
                          {cancelPurchaseLoading ? (
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{
                                duration: 1,
                                repeat: Infinity,
                                ease: "linear",
                              }}
                              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                            />
                          ) : (
                            <XCircle className="w-4 h-4 mr-2" />
                          )}
                          {cancelPurchaseLoading
                            ? "Cancelling..."
                            : "Cancel Transaction"}
                        </Button>
                      </DialogFooter>
                    </motion.div>
                  </DialogContent>
                </Dialog>
              )}

              {!canComplete && !canCancel && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No actions available for this transaction
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Transaction Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">User ID:</span>
                  <span className="font-medium">{transaction.user_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Currency:</span>
                  <span className="font-medium">{transaction.currency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-medium capitalize">
                    {transaction.type}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium capitalize">
                    {transaction.status}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
